import { Injectable } from '@angular/core';

/**
 * Service for handling and formatting error messages in AI integration components.
 * Provides centralized error message extraction and formatting for consistent error handling.
 */
@Injectable({
  providedIn: 'root'
})
export class AiErrorHandlerService {

  /**
   * Extract meaningful error message from error object
   * @param error - Error object from HTTP request
   * @returns User-friendly error message
   */
  extractErrorMessage(error: unknown): string {
    const errorObj = error as Record<string, unknown>;
    let errorMessage = 'AI service is currently unavailable. Please try again.';

    // Handle network errors (status 0, CORS, connection issues)
    if (errorObj?.status === 0 || (errorObj?.error === null && !errorObj?.message)) {
      return 'Unable to connect to the server. Please check your internet connection and try again.';
    }

    // Extract error message from various error object structures
    const errorError = errorObj?.error as Record<string, unknown> | undefined;
    const errorErrorError = errorError?.error as Record<string, unknown> | undefined;

    if (errorErrorError?.message) {
      errorMessage = errorErrorError.message as string;
    } else if (errorError?.message) {
      errorMessage = errorError.message as string;
    } else if (errorObj?.message) {
      errorMessage = errorObj.message as string;
    } else if (typeof errorObj?.error === 'string') {
      errorMessage = errorObj.error;
    }

    // Handle specific error cases
    return this.formatErrorMessage(errorMessage);
  }

  /**
   * Format error message based on common error patterns
   * @param errorMessage - Raw error message
   * @returns Formatted user-friendly error message
   */
  private formatErrorMessage(errorMessage: string): string {
    // Handle specific error cases

    if (errorMessage.includes('429') || errorMessage.includes('quota') || errorMessage.includes('exceeded')) {
      return 'You have exceeded your current quota. Please check your plan and billing details.';
    }

    if (errorMessage.includes('BadRequestError') || errorMessage.includes('Bad Request')) {
      return 'Invalid request. Please check your input and try again.';
    }

    if (errorMessage.includes('Unauthorized') || errorMessage.includes('401')) {
      return 'Authentication failed. Please refresh the page and try again.';
    }

    if (errorMessage.includes('Forbidden') || errorMessage.includes('403')) {
      return 'You do not have permission to perform this action.';
    }

    if (errorMessage.includes('Not Found') || errorMessage.includes('404')) {
      return 'The requested resource was not found. Please try again.';
    }

    if (errorMessage.includes('Internal Server Error') || errorMessage.includes('500')) {
      return 'An internal server error occurred. Please try again later.';
    }

    if (errorMessage.includes('Service Unavailable') || errorMessage.includes('503')) {
      return 'The service is temporarily unavailable. Please try again later.';
    }

    if (errorMessage.includes('Timeout') || errorMessage.includes('timeout')) {
      return 'The request timed out. Please try again.';
    }

    // Return the extracted message or default message
    return errorMessage || 'An unexpected error occurred. Please try again.';
  }
}

